"use client"

import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { useImageUpload } from "@/hooks/useImageUpload"
import { ImageDropZone } from "@/components/ui/image-drop-zone"
import { ProjectSelector } from "@/components/project/ProjectSelector"
import { useTranslation } from "react-i18next"

interface CreatePreviewModalProps {
  trigger: React.ReactNode
  title: React.ReactNode
  description: React.ReactNode
  upload_text: string
  modelUuid: string
  targetRatio?: number
  asChild?: boolean
  open?: boolean
  setOpen?: (open: boolean) => void
  onUploadComplete?: (file: File) => Promise<void>
}

export default function CreatePreviewModal({
  trigger,
  title,
  description,
  upload_text,
  modelUuid,
  targetRatio = 1.3,
  asChild = true,
  open: controlledOpen,
  setOpen: setControlledOpen,
  onUploadComplete
}: CreatePreviewModalProps) {
  const navigate = useNavigate()
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null)
  const [isCropping, setIsCropping] = useState(false)
  
  const {
    open,
    setOpen,
    image,
    previewUrl,
    isUploading,
    handleImageSelected,
    handleImageCleared,
    handleUpload
  } = useImageUpload({
    modelUuid,
    targetRatio
  })

  // Use controlled open state if provided
  const isOpen = controlledOpen !== undefined ? controlledOpen : open
  const setIsOpen = setControlledOpen || setOpen

  const handleUploadAndNavigate = async () => {
    if (onUploadComplete && image) {
      await onUploadComplete(image)
      return
    }

    // Pass the selected project ID to the upload function
    const previewId = await handleUpload(selectedProjectId)
    if (previewId) {
      setIsOpen(false)
      navigate(`/viewer/${previewId}`)
    }
  }

  const canUpload = image && !isUploading && !isCropping
  const { t } = useTranslation("common");

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {asChild && (
        <DialogTrigger asChild>
          {trigger}
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-md" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription asChild>
            <div>{description}</div>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <ImageDropZone
            onImageSelected={handleImageSelected}
            onImageCleared={handleImageCleared}
            image={image}
            preview_label_url={previewUrl}
            targetRatio={targetRatio}
            onCroppingStateChange={setIsCropping}
          />
          
          <div onFocus={(e) => e.stopPropagation()}>
            <ProjectSelector
              selectedProjectId={selectedProjectId}
              onProjectSelect={setSelectedProjectId}
              showCreateButton={true}
            />
          </div>

          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setIsOpen(false)} disabled={isCropping}>
              {t("common:buttons.cancel")}
            </Button>
            <Button 
              onClick={handleUploadAndNavigate} 
              disabled={!canUpload}
            >
              {isUploading ? t("common:buttons.updating") : upload_text}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

